import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import {
  savedListCreatorsInfiniteQueryOptions,
  savedListQueryOptions,
  type CreatorsInListDTO,
} from '@/entities/saved-creators-list';

import { useExportDiscoveryCreatorListWithFeedback } from './hooks/use-export-discovery-creator-list';
import { List } from '@/shared/components/list/list';
import { toast } from 'sonner';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { useRemoveSavedProfilesFromListMutation } from './hooks/use-saved-profile-list-mutations';

import { SavedProfileDetailViewHeader } from './saved-profiles-header';
import { CreatorCard } from '../discovery/ui';

export function SavedCreatorsListDetailPage() {
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const { listId } = useParams({ strict: false });
  if (!listId) throw new Error('[SavedCreatorsListDetailPage] listId is required');
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();
  const { data: listInfo } = useSuspenseQuery(savedListQueryOptions(workspaceId, listId));
  const {
    data: creatorsList,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useSuspenseInfiniteQuery(savedListCreatorsInfiniteQueryOptions(workspaceId, listId));
  const list = useMemo(() => {
    return creatorsList.pages.map((p) => p.creators).flat();
  }, [creatorsList.pages]);
  const removeProfilesMutation = useRemoveSavedProfilesFromListMutation();
  const exportCreatorListWithFeedback = useExportDiscoveryCreatorListWithFeedback();

  const handleRemove = useCallback(
    async (profile_id: string) => {
      await removeProfilesMutation.mutateAsync({
        listId: listId,
        request: {
          creator_ids: [profile_id],
        },
      });
      await refetch();
      toast('Creator removed from list');
    },
    [listId, refetch, removeProfilesMutation],
  );

  const handleExportCsv = useCallback(
    async (ids?: string[]) => {
      await exportCreatorListWithFeedback(
        listId,
        listInfo.title,
        ids ? { creator_ids: ids, selected_all: false } : { selected_all: true },
      );
    },
    [exportCreatorListWithFeedback, listId, listInfo.title],
  );

  const handleBack = useCallback(() => {
    navigate({ to: '..' });
  }, [navigate]);

  return (
    <div className="flex h-screen flex-col">
      <SavedProfileDetailViewHeader
        listInfo={listInfo}
        selectedCreators={selectedProfiles}
        handleBackToList={handleBack}
        handleExportSelected={() => handleExportCsv(selectedProfiles)}
        handleExportList={() => handleExportCsv()}
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-4">
          <List
            className="gap-4 p-2"
            data={list as CreatorsInListDTO[]}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            render={(c) => {
              return (
                <CreatorCard
                  key={c.id}
                  creator={{
                    creator_id: c.id,
                    search_id: '',
                    post_categories: c.post_categories,
                    is_saved: true,
                    saved_at: '',
                    platform: c.platform,
                    username: c.username,
                    nickname: c.nickname,
                    profile_image_url: c.profile_image_url,
                    followers: c.followers,
                    average_er: c.recent_avg_er,
                    matched_keywords: [],
                    excluded_date: '',
                  }}
                  isSelected={selectedProfiles.includes(c.id)}
                  onRemove={() => handleRemove(c.id)}
                  onSelect={() => {
                    setSelectedProfiles((prev) => {
                      const newArr = [...prev];
                      const targetIndex = prev.findIndex((id) => id === c.id);
                      if (targetIndex !== -1) {
                        newArr.splice(targetIndex, 1);
                        return newArr;
                      }
                      return [...prev, c.id];
                    });
                  }}
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
